import * as React from 'react'
import { redirect } from 'react-router-dom'

import { ExpensesRoutesVisibilityDocument } from 'generated/resources/graphql'
import { GetBudgetDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { buildNavigation } from 'modules/core/lib/react-router'

import { PageDateFiltersProvider } from 'modules/expenses/lib/PageDateFiltersContext'
import { WithDateFilters } from 'modules/expenses/lib/WithDateFilters'

import BudgetDetailHeader from './BudgetDetailHeader'

const ReroutingIndex = React.lazy(() => import('modules/finance/pages/ReroutingIndex'))

const ExpensesNewCardPage = React.lazy(() => import('modules/expenses/pages/NewCardPage'))

const GroupExpenseDetailPage = React.lazy(
  () => import('modules/expenses/pages/Groups/DetailPage/GroupExpenseDetailPage')
)

const ExpensesReportsPage = React.lazy(() => import('modules/expenses/pages/ExpensesReportsPage'))
const ExpenseDetailPage = React.lazy(() => import('modules/expenses/pages/ExpenseDetailPage'))
const ExpensesListPage = React.lazy(() => import('modules/expenses/pages/ExpensesListPage'))
const ExpensesListTab = React.lazy(() => import('modules/expenses/pages/ExpensesListTab'))
const GroupDetailPage = React.lazy(() => import('modules/expenses/pages/Groups/DetailPage'))
const GroupEditPage = React.lazy(() => import('modules/expenses/pages/Groups/EditPage'))
const GroupsListTab = React.lazy(() => import('modules/expenses/pages/GroupsListTab'))
const NewCardPage = React.lazy(() => import('modules/expenses/pages/NewCardPage'))
const RootPage = React.lazy(() => import('modules/expenses/pages/RootPage'))
const ExporterPage = React.lazy(() => import('modules/expenses/pages/exporter/ExporterPage'))
const BudgetDetailsPage = React.lazy(() => import('modules/finance/pages/BudgetDetailsPage'))
const BudgetListPage = React.lazy(() => import('modules/finance/pages/BudgetListPage'))

const NewBudgetPage = React.lazy(() => import('modules/finance/pages/NewBudgetPage'))
const NewSpendingPoliciesPage = React.lazy(
  () => import('modules/finance/pages/NewSpendingPoliciesPage')
)
const SpendingPoliciesListPage = React.lazy(
  () => import('modules/finance/pages/SpendingPoliciesListPage')
)
const SpendingPoliciesPage = React.lazy(() => import('modules/finance/pages/SpendingPoliciesPage'))
const SpendingPoliciesDetailPage = React.lazy(
  () => import('modules/finance/pages/SpendingPoliciesDetailPage')
)
const expensesSubModuleRoutes = buildNavigation((navigation) => {
  return [
    {
      path: navigation.finance.spending.expenses.path,
      element: <RootPage />,
      handle: {
        meta: ({ watchQuery }) => {
          return watchQuery({
            query: ExpensesRoutesVisibilityDocument,
          }).map(({ data }) => {
            const {
              sidebarSeeExpensesSpendingPoliciesSubLevelItem,
              sidebarSeeExpensesBudgetsSubLevelItem,
              sidebarSeeExpensesExpensesSubLevelItem,
            } = data.expenses._policies

            return {
              title: i18n.t('expenses_v2.breadcrumbs.expenses'),
              illustration: 'wallet',
              subNavigation: [
                {
                  index: true,
                  id: 'employee-expenses',
                  title: i18n.t('expenses_v2.breadcrumbs.expenses'),
                  href: navigation.finance.spending.expenses.list.fullPath(),
                  isVisible: sidebarSeeExpensesExpensesSubLevelItem.can,
                },
                {
                  id: 'expenses-reports',
                  title: i18n.t('expenses_v2.tabs.expenses_reports_v2'),
                  href: navigation.finance.spending.expenses.reports.fullPath(),
                  isVisible: sidebarSeeExpensesExpensesSubLevelItem.can,
                },
                {
                  id: 'budgets',
                  title: i18n.t('budgets.navbar.budgets'),
                  href: navigation.finance.spending.expenses.budgets.fullPath(),
                  isVisible: sidebarSeeExpensesBudgetsSubLevelItem.can,
                },
                {
                  id: 'spending-policies',
                  title: i18n.t('spending_policies.navbar.spending_policies'),
                  href: navigation.finance.spending.expenses.spendingPolicies.fullPath(),
                  isVisible: sidebarSeeExpensesSpendingPoliciesSubLevelItem.can,
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <ReroutingIndex />,
        },
        {
          path: navigation.finance.spending.expenses.list.path,
          children: [
            {
              index: true,
              loader: () => redirect(navigation.finance.spending.expenses.list.expenses.fullPath()),
            },
            {
              path: navigation.finance.spending.expenses.list.expenses.path,
              handle: {
                meta: () => ({
                  title: i18n.t('expenses_v2.breadcrumbs.expenses'),
                  illustration: 'wallet',
                }),
              },
              children: [
                {
                  path: navigation.finance.spending.expenses.list.expenses.exporter.path,
                  element: <ExporterPage />,
                },
                {
                  path: navigation.finance.spending.expenses.list.expenses.expenseId.path,
                  element: <ExpenseDetailPage />,
                },
              ],
              element: <ExpensesListTab />,
            },
            {
              path: navigation.finance.spending.expenses.list.groups.path,
              handle: {
                meta: () => {
                  return {
                    title: i18n.t('expenses_v2.breadcrumbs.expenses'),
                    illustration: 'wallet',
                  }
                },
              },
              element: <GroupsListTab />,
            },
          ],
          element: (
            <PageDateFiltersProvider>
              <ExpensesListPage />
            </PageDateFiltersProvider>
          ),
        },
        {
          path: navigation.finance.spending.expenses.reports.path,
          element: (
            <PageDateFiltersProvider>
              <ExpensesReportsPage />
            </PageDateFiltersProvider>
          ),
          handle: {
            meta: () => ({
              title: i18n.t('expenses_v2.tabs.expenses_reports'),
            }),
          },
        },
        {
          path: navigation.finance.spending.expenses.groups.path,
          children: [
            {
              index: true,
              loader: () => redirect(navigation.finance.spending.expenses.list.groups.fullPath()),
            },
            {
              path: navigation.finance.spending.expenses.groups.id.path,
              handle: {
                resource: true,
                meta: () => ({
                  title: i18n.t('expenses_v2.breadcrumbs.group_id'),
                }),
              },
              children: [
                {
                  path: navigation.finance.spending.expenses.groups.id.edit.path,
                  element: <GroupEditPage />,
                },
                {
                  path: navigation.finance.spending.expenses.groups.id.expenseId.path,
                  element: <GroupExpenseDetailPage />,
                },
              ],
              element: <GroupDetailPage />,
            },
          ],
          element: <WithDateFilters />,
        },
        {
          path: navigation.finance.spending.expenses.new_card.path,
          element: <NewCardPage />,
          handle: {
            meta: () => ({
              title: i18n.t('expenses_v2.modals.new_card.title'),
            }),
          },
        },
        {
          path: navigation.finance.spending.expenses.budgets.path,
          handle: {
            meta: () => ({ title: i18n.t('budgets.detail.budgets') }),
          },
          children: [
            {
              index: true,
              loader: () => redirect(navigation.finance.spending.expenses.budgets.list.fullPath()),
            },
            {
              path: navigation.finance.spending.expenses.budgets.list.path,
              element: <BudgetListPage />,
              children: [
                {
                  path: navigation.finance.spending.expenses.budgets.list.new.path,
                  element: <NewBudgetPage />,
                },
              ],
            },
            {
              path: navigation.finance.spending.expenses.budgets.detail.path,
              handle: {
                resource: true,
                meta: ({ watchQuery, params }) => {
                  return watchQuery({
                    query: GetBudgetDocument,
                    variables: { id: Number(params.budgetId) },
                    fetchPolicy: 'cache-and-network',
                  }).map(({ data }) => {
                    const budget = data?.finance?.budgetsConnection?.nodes[0]

                    return {
                      title: budget?.name,
                      headerComponent: <BudgetDetailHeader budget={budget} />,
                    }
                  })
                },
              },
              element: <BudgetDetailsPage />,
            },
          ],
        },
        {
          path: navigation.finance.spending.expenses.spendingPolicies.path,
          handle: {
            meta: () => ({ title: i18n.t('expenses_v2.breadcrumbs.policies') }),
          },
          element: <SpendingPoliciesPage />,
          children: [
            {
              index: true,
              loader: () =>
                redirect(navigation.finance.spending.expenses.spendingPolicies.list.fullPath()),
            },
            {
              path: navigation.finance.spending.expenses.spendingPolicies.list.path,
              children: [
                {
                  path: navigation.finance.spending.expenses.spendingPolicies.list.new.path,
                  element: <NewSpendingPoliciesPage />,
                },
              ],
              element: <SpendingPoliciesListPage />,
            },
            {
              path: navigation.finance.spending.expenses.spendingPolicies.detail.path,

              handle: {
                resource: true,
                meta: () => ({ title: i18n.t('expenses_v2.breadcrumbs.edit_policy') }),
              },
              element: <SpendingPoliciesDetailPage />,
            },
          ],
        },
      ],
    },
    {
      path: navigation.finance.spending.expenses.new_card.path,
      element: <ExpensesNewCardPage />,
      handle: { meta: () => ({ title: i18n.t('expenses_v2.modals.new_card.title') }) },
    },
  ]
})

export default expensesSubModuleRoutes
